import React, { useEffect, useState, lazy } from 'react';
import { Button } from '../UI/Button';
export const TrainingHero = () => {
  const audiences = ['Schools', 'Startups', 'Organizations', 'Companies', 'Communities'];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % audiences.length);
        setIsVisible(true);
      }, 500); // Wait for fade out before changing text
    }, 3000); // Change every 3 seconds
    return () => clearInterval(intervalId);
  }, []);
  const scrollToForm = () => {
    const formSection = document.getElementById('training-proposal-form');
    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-[80vh] flex items-center bg-navy overflow-hidden pt-10 pb-20" data-id="element-1171">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-1172">
        <style data-id="element-1173">
          {`
            @keyframes float {
              0% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-20px) rotate(5deg); }
              100% { transform: translateY(0px) rotate(0deg); }
            }
            @keyframes float-reverse {
              0% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(20px) rotate(-5deg); }
              100% { transform: translateY(0px) rotate(0deg); }
            }
            .bg-shape-1 { animation: float 8s ease-in-out infinite; }
            .bg-shape-2 { animation: float-reverse 12s ease-in-out infinite; }
            .bg-shape-3 { animation: float 10s ease-in-out infinite 1s; }
          `}
        </style>

        {/* Abstract shapes representing AI/Networking/Digital */}
        <div className="absolute top-[10%] left-[5%] w-64 h-64 rounded-full border border-white/10 bg-gradient-to-br from-primary/20 to-transparent blur-xl bg-shape-1" data-id="element-1174"></div>
        <div className="absolute bottom-[20%] right-[10%] w-96 h-96 rounded-full border border-white/5 bg-gradient-to-tl from-blue-500/10 to-transparent blur-2xl bg-shape-2" data-id="element-1175"></div>
        <div className="absolute top-[40%] right-[20%] w-32 h-32 rounded-full bg-gold/10 blur-xl bg-shape-3" data-id="element-1176"></div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjA1KSIvPjwvc3ZnPg==')] opacity-50" data-id="element-1177"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-1178">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-7xl mx-auto" data-id="element-1179">
          {/* Left Column - Text Content */}
          <div className="lg:col-span-7 text-left" data-id="element-1180">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 font-heading animate-slide-up" data-id="element-1181">
              We Train The Teams That Build The Future
            </h1>

            <div className="h-16 md:h-20 flex items-center justify-start mb-10 animate-slide-up" style={{
            animationDelay: '200ms'
          }} data-id="element-1182">
              <p className="text-2xl md:text-3xl lg:text-4xl text-gray-300 font-light flex flex-wrap justify-start items-center gap-2" data-id="element-1183">
                <span data-id="element-1184">We train the teams</span>
                {currentIndex === 0 && <span data-id="element-1185">powering</span>}
                {currentIndex === 1 && <span data-id="element-1186">building</span>}
                {currentIndex === 2 && <span data-id="element-1187">leading</span>}
                {currentIndex === 3 && <span data-id="element-1188">growing</span>}
                {currentIndex === 4 && <span data-id="element-1189">shaping</span>}
                <span className={`font-bold text-gold transition-all duration-500 ease-in-out ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4'}`} data-id="element-1190">
                  {audiences[currentIndex]}
                </span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-start items-center gap-4 sm:gap-6 animate-slide-up" style={{
            animationDelay: '400ms'
          }} data-id="element-1191">
              <Button size="large" className="w-full sm:w-auto text-lg px-8 py-4 shadow-lg shadow-primary/20 hover:shadow-primary/40" onClick={scrollToForm} data-id="element-1192">
                Book a Training
              </Button>
              <Button variant="outline" size="large" className="w-full sm:w-auto text-lg px-8 py-4 border-white text-white hover:bg-white/10" onClick={scrollToForm} data-id="element-1193">
                Request a Proposal
              </Button>
            </div>

            {/* Stats row */}
            <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center gap-8 sm:gap-12 mt-14 animate-slide-up" style={{
            animationDelay: '600ms'
          }} data-id="element-1194">
              <div className="flex flex-col items-start" data-id="element-1195">
                <span className="text-4xl font-bold text-gold mb-1" data-id="element-1196">200+</span>
                <span className="text-sm text-gray-300 font-medium" data-id="element-1197">
                  Educators &amp;
                  <br className="hidden sm:block" data-id="element-1198" /> Lecturers Trained
                </span>
              </div>
              <div className="flex flex-col items-start" data-id="element-1199">
                <span className="text-4xl font-bold text-gold mb-1" data-id="element-1200">500+</span>
                <span className="text-sm text-gray-300 font-medium" data-id="element-1201">
                  Professionals
                  <br className="hidden sm:block" data-id="element-1202" /> Empowered
                </span>
              </div>
              <div className="flex flex-col items-start" data-id="element-1203">
                <span className="text-4xl font-bold text-gold mb-1" data-id="element-1204">5</span>
                <span className="text-sm text-gray-300 font-medium" data-id="element-1205">
                  Teams
                  <br className="hidden sm:block" data-id="element-1206" /> Trained
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Image */}
          <div className="lg:col-span-5 mt-12 lg:mt-0 animate-slide-up" style={{
          animationDelay: '300ms'
        }} data-id="element-1207">
            <div className="relative w-full h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl border border-white/10 group" data-id="element-1208">
              <div className="absolute inset-0 bg-navy/20 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-1209"></div>
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gLvZ6fTjQSpPs86zUNEWZz/Group.jpg" alt="Networking Salon Training Session" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-1210" />
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gold/20 rounded-full blur-2xl z-0" data-id="element-1211"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-2xl z-0" data-id="element-1212"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};